// NOTE: When you add or change markdown syntax here, also update the Markdown Preview page in the
// management app (apps/management-app/src/screens/MarkdownPreview/syntax-reference.ts). See ./README.md.
import React, { memo, useMemo } from 'react';
import Markdown, { type Options as MarkdownOptions } from 'react-markdown';
import remarkDirective from 'remark-directive';
import remarkFlexibleMarkers from 'remark-flexible-markers';
import remarkGfm from 'remark-gfm';
import { css } from '@emotion/react';
import { remarkCitations } from './remark.citations-plugin';
import { remarkCustomComponents } from './remark.custom-component-plugin';
import { remarkDirectiveToComponent, remarkCollapsible } from './remark.directive-component-plugin';
import {
  SecureHtmlRenderer,
  parseContent,
  mixedContentContainerStyles,
  markdownBlockStyles,
  htmlBlockStyles,
} from './SecureHtmlRenderer';
import { useMarkdownComponents } from './useMarkdownComponents';

export interface SecureHtmlOptions {
  adaptiveHeight?: boolean;
}

export interface ChatMarkdownProps {
  components?: MarkdownOptions['components'];
  remarkPlugins?: MarkdownOptions['remarkPlugins'];
  value: string;
  allowHtml?: boolean;
  trustedDomains?: string[];
  htmlOptions?: SecureHtmlOptions;
}

// Shared remark plugins configuration
const REMARK_PLUGINS = [
  remarkGfm,
  remarkFlexibleMarkers,
  remarkDirective,
  remarkDirectiveToComponent,
  remarkCitations,
  remarkCustomComponents,
  remarkCollapsible,
];

/**
 * Escape colons inside {{...}} patterns so that remark-directive (used for ::directive{} syntax)
 * doesn't split `{{attachment:id}}` into `"{{attachment"` + textDirective(`:id`) + `"}}"`.
 * After markdown parsing, `\:` is unescaped back to `:` in text nodes so the
 * remarkCustomComponents plugin can match `{{attachment:id}}` correctly.
 *
 * Code segments (fenced blocks and inline code) are left unchanged so that
 * users can show literal `{{type:id}}` text inside code examples without it
 * being rewritten.
 */
function preprocessMarkdownForCustomComponents(text: string): string {
  // Matches fenced code blocks (``` or ~~~, with optional language tag) and
  // inline code spans. These segments are passed through unchanged.
  const CODE_SEGMENT_RE = /(```[\s\S]*?```|~~~[\s\S]*?~~~|`[^`\n]+`)/g;
  const parts: string[] = [];
  let lastIndex = 0;

  for (const match of text.matchAll(CODE_SEGMENT_RE)) {
    const before = text.slice(lastIndex, match.index);
    parts.push(escapeCustomComponentColons(before));
    parts.push(match[0]); // code segment: leave untouched
    lastIndex = (match.index ?? 0) + match[0].length;
  }
  parts.push(escapeCustomComponentColons(text.slice(lastIndex)));

  return parts.join('');
}

function escapeCustomComponentColons(segment: string): string {
  // Match {{word:word}} and escape the colon. Already-escaped \: is not matched
  // (the [a-zA-Z0-9_-]+ before the colon won't capture a backslash).
  return segment.replace(/\{\{([a-zA-Z0-9_-]+):([a-zA-Z0-9_-]+)\}\}/g, '{{$1\\:$2}}');
}

/**
 * Markdown renderer with support for mixed markdown/HTML content
 * Supports secure rendering of HTML blocks with domain validation and CSP
 */
export const ChatMarkdown: React.FC<ChatMarkdownProps> = memo(
  ({
    components: customComponents,
    remarkPlugins: extraRemarkPlugins,
    value,
    allowHtml = false,
    trustedDomains,
    htmlOptions,
  }: ChatMarkdownProps) => {
    const components = useMarkdownComponents(customComponents);
    const plugins = useMemo(
      () => (extraRemarkPlugins ? [...REMARK_PLUGINS, ...extraRemarkPlugins] : REMARK_PLUGINS),
      [extraRemarkPlugins]
    );
    // Pre-process to escape colons in {{...}} attachment tags before remark-directive sees them
    const processedValue = useMemo(() => preprocessMarkdownForCustomComponents(value ?? ''), [value]);
    if (!allowHtml) {
      return (
        <Markdown css={markdownStyles} remarkPlugins={plugins} skipHtml={true} components={components}>
          {processedValue}
        </Markdown>
      );
    }

    const contentBlocks = parseContent(processedValue);
    const oneAndOnlyBlock = contentBlocks.length === 1 ? contentBlocks[0] : null;
    if (oneAndOnlyBlock && oneAndOnlyBlock.type === 'markdown') {
      return (
        <Markdown css={markdownStyles} remarkPlugins={plugins} skipHtml={false} components={components}>
          {oneAndOnlyBlock.content}
        </Markdown>
      );
    }

    return (
      <div css={mixedContentContainerStyles}>
        {contentBlocks.map((block, index) => (
          <ContentBlock
            key={`${block.type}-${index}`}
            block={block}
            components={components}
            plugins={plugins}
            trustedDomains={trustedDomains}
            htmlOptions={htmlOptions}
          />
        ))}
      </div>
    );
  }
);

interface ContentBlockProps {
  block: { type: 'markdown' | 'html'; content: string };
  components: MarkdownOptions['components'];
  plugins: MarkdownOptions['remarkPlugins'];
  trustedDomains?: string[];
  htmlOptions?: SecureHtmlOptions;
}

const ContentBlock = memo(
  ({ block, components, plugins, trustedDomains, htmlOptions }: ContentBlockProps) => {
    if (block.type === 'markdown') {
      return (
        <div css={markdownBlockStyles}>
          <Markdown css={markdownStyles} remarkPlugins={plugins} skipHtml={false} components={components}>
            {block.content}
          </Markdown>
        </div>
      );
    }

    return (
      <div css={htmlBlockStyles}>
        <SecureHtmlRenderer
          html={block.content}
          trustedDomains={trustedDomains}
          adaptiveHeight={htmlOptions?.adaptiveHeight}
        />
      </div>
    );
  }
);

/**
 * Basic markdown renderer without HTML support
 * Lightweight version for simple markdown content
 */
type BasicMarkdownProps = {
  children: string;
  components?: MarkdownOptions['components'];
};

export const BasicMarkdown = ({ children, components }: BasicMarkdownProps) => {
  const resolvedComponents = useMarkdownComponents(components);

  return (
    <Markdown remarkPlugins={[remarkGfm, remarkCustomComponents]} components={resolvedComponents}>
      {children}
    </Markdown>
  );
};

const markdownStyles = css({
  whiteSpace: 'normal',
});
